import {
  Component,
  Input,
  OnInit,
  OnChanges,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  SimpleChanges
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DeviationListMetadata } from '../../models/deviationListMetaData';
import { chartColors, deviationStatusColor } from '../../../../shared/common/chart-colors';

@Component({
  selector: 'app-deviation-list-charts',
  templateUrl: './deviation-list-charts.component.html',
  styleUrls: ['./deviation-list-charts.component.css'],
  standalone: false
})
export class DeviationListChartsComponent implements OnInit, OnChanges {
  @Input() deviationMetadata: DeviationListMetadata;
  @Input() deviationListCount: number; // Removed
  @Input() chartView: string;
  @Input() isPortal: boolean;
  @Output() buttonClicked = new EventEmitter<void>();

  single: any[] = [];
  departmentData: any[] = [];
  businessData: any[] = [];
  importanceData: any[] = [];
  view: any[];
  legendTitle: string;
  gradient: boolean = false;
  showLegend: boolean = true;
  colorScheme = {
    domain: ['REJECTED', 'DONE', 'OPEN', 'NEW'].map(deviationStatusColor)
  };
  colorSchemeImportance = {
    domain: [chartColors.done, chartColors.open, chartColors.danger] // LOW, MEDIUM, HIGH
  };

  currentDepartmentPage: number = 0;
  currentBusinessPage: number = 0;
  itemsPerPage: number = 10;
  paginatedDepartmentData: any[] = [];
  paginatedBusinessData: any[] = [];

  private readonly STATUS_ORDER = ['REJECTED', 'DONE', 'OPEN', 'NEW'];
  private readonly IMPORTANCE_ORDER = ['LOW', 'MEDIUM', 'HIGH'];


  constructor(
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.legendTitle = this.translate.instant('DESCRIPTION');
  }

  ngOnInit(): void {
    this.updateChartData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviationMetadata']) {
      this.updateChartData();
    }
  }

  /**
   * Decodes HTML entities in a string.
   * @param str - The string to decode.
   * @returns The decoded string.
   */
  private decodeHtmlEntity(str: string): string {
    if (str === null || str === undefined) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
  }

  /**
   * Updates all chart data based on deviationMetadata.
   */
  async updateChartData(): Promise<void> {
    if (!this.deviationMetadata) {
      this.single = [];
      this.departmentData = [];
      this.businessData = [];
      this.importanceData = [];
      this.paginateData('department');
      this.paginateData('business');
      this.cdr.detectChanges();
      return;
    }

    await this.updateStatusData();
    await this.updateDepartmentData();
    await this.updateBusinessData();
    await this.updateImportanceData();
    this.cdr.detectChanges();
  }

  /**
   * Translates an array of keys.
   * @param keys - The keys to translate.
   * @returns A promise that resolves to a map of original keys to translated strings.
   */
  private async translateKeys(keys: string[]): Promise<{ [key: string]: string }> {
    if (!keys || keys.length === 0) {
      return Promise.resolve({});
    }
    return this.translate.get(keys).toPromise();
  }


  /**
   * Updates the status data for the chart using deviationMetadata.
   */
  async updateStatusData(): Promise<void> {
    if (!this.deviationMetadata.StatusCounts) {
      this.single = [];
      return;
    }

    const translatedStatusMap = await this.translateKeys(this.STATUS_ORDER);
    const statusCountMap = new Map<string, number>();
    this.deviationMetadata.StatusCounts.forEach(sc => {
      statusCountMap.set(sc.Status.toUpperCase(), sc.Count);
    });

    this.single = this.STATUS_ORDER.map(statusKey => ({
      name: translatedStatusMap[statusKey] || statusKey,
      value: statusCountMap.get(statusKey) || 0
    }));
  }

  /**
   * Updates the department data for the chart using deviationMetadata.
   * This data is used when !isPortal for 'DEVIATIONS_PER_DEPARTMENT'.
   */
  async updateDepartmentData(): Promise<void> {

    if (!this.deviationMetadata.DepartmentStatusCounts) {
      this.departmentData = [];
      this.paginateData('department');
      return;
    }

    const translatedStatusMap = await this.translateKeys(this.STATUS_ORDER);

    this.departmentData = this.deviationMetadata.DepartmentStatusCounts.map(deptMeta => {
      const statusDetailsMap = new Map<string, number>();
      (deptMeta.StatusDetails || []).forEach(sd => {
        statusDetailsMap.set(sd.Status.toUpperCase(), sd.Count);
      });

      return {
        name: this.decodeHtmlEntity(deptMeta.DepartmentName),
        series: this.STATUS_ORDER.map(statusKey => ({
          name: translatedStatusMap[statusKey] || statusKey,
          value: statusDetailsMap.get(statusKey) || 0
        }))
      };
    });


    this.paginateData('department');
  }

  /**
   * Updates the business data for the chart using deviationMetadata.
   * This data is used when isPortal for 'DEVIATIONS_PER_BUSINESS'.
   * It repurposes DepartmentStatusCounts, assuming DepartmentName as BusinessName in portal view.
   */
  async updateBusinessData(): Promise<void> {
    if (!this.deviationMetadata.DepartmentStatusCounts) {
      this.businessData = [];
      this.paginateData('business');
      return;
    }

    const translatedStatusMap = await this.translateKeys(this.STATUS_ORDER);

    this.businessData = this.deviationMetadata.DepartmentStatusCounts.map(businessMeta => {
      const statusDetailsMap = new Map<string, number>();
      (businessMeta.StatusDetails || []).forEach(sd => {
        statusDetailsMap.set(sd.Status.toUpperCase(), sd.Count);
      });

      return {
        name: this.decodeHtmlEntity(businessMeta.BusinessName),
        series: this.STATUS_ORDER.map(statusKey => ({
          name: translatedStatusMap[statusKey] || statusKey,
          value: statusDetailsMap.get(statusKey) || 0
        }))
      };
    });
    this.paginateData('business');
  }

  /**
   * Updates the importance data for the chart using deviationMetadata.
   */
  async updateImportanceData(): Promise<void> {
    if (!this.deviationMetadata || !this.deviationMetadata.ImportanceCounts) {
      this.importanceData = [];
      console.log('No deviationMetadata or ImportanceCounts, setting importanceData to empty.');
      return;
    }

    // this.IMPORTANCE_ORDER is ['LOW', 'MEDIUM', 'HIGH']
    const translatedImportanceMap = await this.translateKeys(this.IMPORTANCE_ORDER);

    const importanceCountMap = new Map<string, number>();
    this.deviationMetadata.ImportanceCounts.forEach(ic => {
      if (ic.Importance && typeof ic.Importance === 'string') {
        importanceCountMap.set(ic.Importance.toUpperCase(), ic.Count);
      } else {
        console.warn('ImportanceCountDto found with invalid Importance field:', ic);
      }
    });


    this.importanceData = this.IMPORTANCE_ORDER.map(importanceKeyFromOrder => {
      // importanceKeyFromOrder is 'LOW', 'MEDIUM', or 'HIGH' (already uppercase from this.IMPORTANCE_ORDER)
      const count = importanceCountMap.get(importanceKeyFromOrder) || 0;
      return {
        name: translatedImportanceMap[importanceKeyFromOrder] || importanceKeyFromOrder,
        value: count
      };
    });

  }

  /**
   * Paginates the data for the given type (department or business).
   * @param type - The type of data to paginate ('department' or 'business').
   */
  paginateData(type: string): void {
    if (type === 'department') {
      const start = this.currentDepartmentPage * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      this.paginatedDepartmentData = (this.departmentData || []).slice(start, end);
    } else if (type === 'business') {
      const start = this.currentBusinessPage * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      this.paginatedBusinessData = (this.businessData || []).slice(start, end);
    }
    // No need for this.cdr.detectChanges() here if called after all data updates in updateChartData
    // or if called from template event handlers which trigger change detection.
    // However, keeping it if paginateData can be called from other contexts.
    // For safety and explicitness, it's fine.
    this.cdr.detectChanges();
  }

  /**
   * Navigates to the previous page of data for the given type.
   * @param type - The type of data ('department' or 'business').
   */
  prevPage(type: string): void {
    this.buttonClicked.emit();

    if (type === 'department') {
      if (this.currentDepartmentPage > 0) {
        this.currentDepartmentPage--;
      } else {
        // Wrap around to the last page
        const totalPages = Math.ceil((this.departmentData?.length || 0) / this.itemsPerPage);
        this.currentDepartmentPage = totalPages > 0 ? totalPages - 1 : 0;
      }
      this.paginateData('department');
    } else if (type === 'business') {
      if (this.currentBusinessPage > 0) {
        this.currentBusinessPage--;
      } else {
        // Wrap around to the last page
        const totalPages = Math.ceil((this.businessData?.length || 0) / this.itemsPerPage);
        this.currentBusinessPage = totalPages > 0 ? totalPages - 1 : 0;
      }
      this.paginateData('business');
    }
  }

  /**
   * Navigates to the next page of data for the given type.
   * @param type - The type of data ('department' or 'business').
   */
  nextPage(type: string): void {
    this.buttonClicked.emit();

    if (type === 'department') {
      const totalPages = Math.ceil((this.departmentData?.length || 0) / this.itemsPerPage);
      if (this.currentDepartmentPage < totalPages - 1) {
        this.currentDepartmentPage++;
      } else {
        // Wrap around to the first page
        this.currentDepartmentPage = 0;
      }
      this.paginateData('department');
    } else if (type === 'business') {
      const totalPages = Math.ceil((this.businessData?.length || 0) / this.itemsPerPage);
      if (this.currentBusinessPage < totalPages - 1) {
        this.currentBusinessPage++;
      } else {
        // Wrap around to the first page
        this.currentBusinessPage = 0;
      }
      this.paginateData('business');
    }
  }

  /**
   * Checks if there are more pages of data for the given type. (Not currently used in template)
   * @param type - The type of data ('department' or 'business').
   * @returns A boolean indicating if there are more pages.
   */
  hasMorePages(type: string): boolean {
    if (type === 'department') {
      return (
        (this.currentDepartmentPage + 1) * this.itemsPerPage < (this.departmentData?.length || 0));
    } else if (type === 'business') {
      return (
        (this.currentBusinessPage + 1) * this.itemsPerPage < (this.businessData?.length || 0));
    }
    return false;
  }

  // Helper to check if pagination buttons should be visible
  shouldShowPagination(type: string): boolean {
    if (type === 'department') {
      return (this.departmentData?.length || 0) > this.itemsPerPage;
    } else if (type === 'business') {
      return (this.businessData?.length || 0) > this.itemsPerPage;
    }
    return false;
  }
}
